import { format, isValid } from 'date-fns';
import { ar, fr } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { useInventoryReceipts, TimeFilter } from '@/hooks/useInventoryReceipts';
import { useReceiptEditHistory } from '@/hooks/useEditHistory';
import {
  Loader2,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/lib/permissions';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { useAssistants } from '@/hooks/useAssistants';
import { EditReceiptDialog } from './EditReceiptDialog';
import { DeleteReceiptDialog } from './DeleteReceiptDialog';
import { EditHistoryPanel } from './EditHistoryPanel';
import type { InventoryReceipt } from '@/types/entities';

interface ReceiptHistoryTableProps {
  restaurantId: string;
  ingredientId?: string;
}

// Helper component to fetch and display edit history for a receipt
function ReceiptEditHistoryRow({ receiptId, restaurantId, colSpan }: { receiptId: string; restaurantId: string; colSpan: number }) {
  const { data: editHistory, isLoading } = useReceiptEditHistory(receiptId, restaurantId);

  return (
    <TableRow className="bg-muted/20 hover:bg-muted/30">
      <TableCell colSpan={colSpan} className="p-2">
        <EditHistoryPanel
          entries={editHistory || []}
          isLoading={isLoading}
          type="receipt"
        />
      </TableCell>
    </TableRow>
  );
}

// Helper component to display who deleted a receipt
function ReceiptDeletedBy({ receiptId, restaurantId, deletedAt, updatedByDisplay }: { receiptId: string; restaurantId: string; deletedAt: string | null; updatedByDisplay?: string }) {
  const { data: editHistory } = useReceiptEditHistory(deletedAt ? receiptId : null, restaurantId);
  const { t } = useTranslation();

  if (!deletedAt) {
    return <span>-</span>;
  }

  // First try to use updatedByDisplay if available
  if (updatedByDisplay) {
    return (
      <span className="text-xs text-muted-foreground italic">
        {t('inventory.receiptHistory.deletedByLabel')}: {updatedByDisplay}
      </span>
    );
  }

  // Fallback: get from edit history
  if (editHistory) {
    const deletionEntry = editHistory.find(entry => entry.fieldChanged === 'deleted');
    if (deletionEntry) {
      // Use the modifiedBy directly (should be "Admin" or assistant username)
      const deletedBy = deletionEntry.modifiedBy;
      return (
        <span className="text-xs text-muted-foreground italic">
          {t('inventory.receiptHistory.deletedByLabel')}: {deletedBy}
        </span>
      );
    }
  }

  return <span>-</span>;
}

interface GroupedReceipt {
  invoiceNumber: string;
  supplier: string;
  receiptDate: string;
  receipts: InventoryReceipt[];
  totalCost: number;
}

export function ReceiptHistoryTable({ restaurantId, ingredientId }: ReceiptHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? ar : fr;
  const isRTL = i18n.language === 'ar';

  // Get assistant data for permission checks
  const { data: assistant } = useCurrentAssistant();
  const { data: allAssistants } = useAssistants(restaurantId);

  // Filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [createdByFilter, setCreatedByFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Memoize filters to prevent infinite re-renders
  const filters = useMemo(() => {
    // If a specific date is selected, use it as both start and end date
    if (selectedDate) {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      return {
        ingredientId,
        startDate: startOfDay,
        endDate: endOfDay,
        createdBy: createdByFilter ?? undefined,
        page,
        pageSize,
      };
    }
    return {
      ingredientId,
      timeFilter,
      createdBy: createdByFilter ?? undefined,
      page,
      pageSize,
    };
  }, [ingredientId, timeFilter, selectedDate, createdByFilter, page, pageSize]);

  const { data: response, isLoading } = useInventoryReceipts(restaurantId, filters);

  const receipts = response?.data || [];
  const totalPages = response?.totalPages || 1;
  const totalCount = response?.totalCount || 0;

  // State for expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // State for edit/delete dialogs
  const [editingReceipt, setEditingReceipt] = useState<InventoryReceipt | null>(null);
  const [deletingReceipts, setDeletingReceipts] = useState<InventoryReceipt[]>([]);

  // State for expanded edit history
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);

  // Check permissions - if assistant is logged in, check their permissions; otherwise assume admin (full access)
  const canEdit = assistant ? hasPermission(assistant.permissions, 'stock', 'edit_receipt') : true;
  const canDelete = assistant
    ? hasPermission(assistant.permissions, 'stock', 'delete_receipt')
    : true;
  
  // Show actions column only if user has at least one action permission
  const showActionsColumn = canEdit || canDelete;

  // Group receipts by batch_id (only receipts added together in same batch are grouped)
  const { groupedReceipts, ungroupedReceipts } = useMemo(() => {
    const grouped = new Map<string, GroupedReceipt>();
    const ungrouped: InventoryReceipt[] = [];

    receipts.forEach((receipt) => {
      // Only group if batch_id is present (receipts intentionally added together)
      const batchId = (receipt as any).batchId;
      if (batchId) {
        if (!grouped.has(batchId)) {
          grouped.set(batchId, {
            invoiceNumber: receipt.invoiceNumber || '',
            supplier: receipt.supplier || '',
            receiptDate: receipt.receiptDate,
            receipts: [],
            totalCost: 0,
          });
        }

        const group = grouped.get(batchId)!;
        group.receipts.push(receipt);
        // Only add to total if not deleted
        if (!receipt.deletedAt) {
          group.totalCost += Number(receipt.totalCost) || 0;
        }
      } else {
        // Receipts without batch_id are standalone (not grouped)
        ungrouped.push(receipt);
      }
    });

    // Move groups with only 1 receipt to ungrouped (shouldn't happen but handle it)
    const actualGroups: GroupedReceipt[] = [];
    grouped.forEach((group) => {
      if (group.receipts.length > 1) {
        actualGroups.push(group);
      } else if (group.receipts.length === 1 && group.receipts[0]) {
        ungrouped.push(group.receipts[0]);
      }
    });

    return {
      groupedReceipts: actualGroups,
      ungroupedReceipts: ungrouped,
    };
  }, [receipts]);

  // Toggle group expansion
  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Get group key (using batch_id from first receipt in the group)
  const getGroupKey = (group: GroupedReceipt): string => {
    const firstReceipt = group.receipts[0];
    return (firstReceipt as any)?.batchId || `group-${group.supplier}-${group.receiptDate}`;
  };

  // Reset to page 1 when filters change
  const handleTimeFilterChange = (newFilter: TimeFilter) => {
    setTimeFilter(newFilter);
    setSelectedDate(undefined);
    setPage(1);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setPage(1);
  };

  const clearDateFilter = () => {
    setSelectedDate(undefined);
    setPage(1);
  };

  // Get all possible creator names: "Admin" + all assistant usernames
  const createdByNames = useMemo(() => {
    const names: string[] = ['Admin'];
    if (allAssistants) {
      allAssistants.forEach((a) => {
        if (a.username && !names.includes(a.username)) {
          names.push(a.username);
        }
      });
    }
    return names;
  }, [allAssistants]);

  // Combine grouped and ungrouped for display
  const allReceiptsToDisplay = useMemo(() => [
    ...groupedReceipts.map((group) => ({ type: 'group' as const, data: group })),
    ...ungroupedReceipts.map((receipt) => ({ type: 'receipt' as const, data: receipt })),
  ], [groupedReceipts, ungroupedReceipts]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Only count non-deleted receipts in total
  const totalCost = receipts
    .filter(r => !r.deletedAt)
    .reduce((sum, r) => sum + (Number(r.totalCost) || 0), 0);

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span>{t('inventory.receiptHistory.title')}</span>
            <Badge variant="outline" className="font-mono">
              {t('inventory.receiptHistory.total')}: {totalCost.toFixed(2)} {t('common.currency')}
            </Badge>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Time Filter Buttons */}
            <div className="flex items-center gap-1 rounded-lg border p-1">
              <Button
                variant={timeFilter === 'today' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('today')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.today')}
              </Button>
              <Button
                variant={timeFilter === 'week' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('week')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.week')}
              </Button>
              <Button
                variant={timeFilter === 'month' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('month')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.month')}
              </Button>
              <Button
                variant={timeFilter === 'all' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('all')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.all')}
              </Button>
            </div>

            {/* Calendar Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={selectedDate ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-8 gap-2 text-xs',
                    selectedDate && 'bg-primary text-primary-foreground'
                  )}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {selectedDate
                    ? format(selectedDate, 'dd/MM/yyyy', { locale })
                    : t('inventory.filters.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align={isRTL ? 'end' : 'start'}>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      const localDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                        0,
                        0,
                        0,
                        0
                      );
                      handleDateSelect(localDate);
                    } else {
                      handleDateSelect(undefined);
                    }
                  }}
                  initialFocus
                  locale={locale}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  weekStartsOn={isRTL ? 6 : 1}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>

            {/* Clear Date Filter */}
            {selectedDate && (
              <Button variant="ghost" size="sm" onClick={clearDateFilter} className="h-8 px-2">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Created By Filter Badges */}
          {createdByNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('inventory.filters.createdBy')}:</span>
              <Badge
                variant={createdByFilter === null ? 'default' : 'outline'}
                className="cursor-pointer text-xs"
                onClick={() => { setCreatedByFilter(null); setPage(1); }}
              >
                {t('inventory.filters.all')}
              </Badge>
              {createdByNames.map((name) => (
                <Badge
                  key={name}
                  variant={createdByFilter === name ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => { setCreatedByFilter(createdByFilter === name ? null : name); setPage(1); }}
                >
                  {name}
                </Badge>
              ))}
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {receipts.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">{t('inventory.noReceipts')}</div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.date')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.ingredient')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.quantity')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.pricePerUnit')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.totalCost')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.supplier')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.invoiceNumber')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.createdBy')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.deletedBy')}
                    </TableHead>
                    {showActionsColumn && (
                      <TableHead className="text-right w-[100px]">
                        {t('common.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allReceiptsToDisplay.map((item, index) => {
                    if (item.type === 'group') {
                      const group = item.data;
                      const groupKey = getGroupKey(group);
                      const isExpanded = expandedGroups.has(groupKey);
                      const receiptDate = new Date(group.receiptDate);
                      const allDeleted = group.receipts.every(r => !!r.deletedAt);

                      return (
                        <React.Fragment key={`group-${groupKey}`}>
                          {/* Group Header Row */}
                          <TableRow
                            className={cn(
                              "bg-primary/5 hover:bg-primary/10 cursor-pointer border-l-4 border-l-primary font-semibold",
                              allDeleted && "opacity-60 bg-muted/30"
                            )}
                            onClick={() => toggleGroup(groupKey)}
                          >
                            <TableCell className="w-[50px]">
                              <Button
                                variant="outline"
                                size="icon"
                                className={cn(
                                  'h-7 w-7 border-2',
                                  isExpanded
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-background hover:bg-primary/10'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(groupKey);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className={cn("font-medium", allDeleted && "line-through")}>
                              {isValid(receiptDate)
                                ? format(receiptDate, 'dd/MM/yyyy', { locale })
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={allDeleted ? "destructive" : "secondary"} className={cn("font-semibold", allDeleted && "line-through")}>
                                {group.receipts.length}{' '}
                                {group.receipts.length === 1
                                  ? t('inventory.receiptHistory.ingredient')
                                  : t('inventory.receiptHistory.ingredients')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">-</TableCell>
                            <TableCell className="text-muted-foreground">-</TableCell>
                            <TableCell>
                              <Badge variant="default" className={cn("font-mono font-semibold", allDeleted && "line-through")}>
                                {group.totalCost.toFixed(2)} {t('common.currency')}
                              </Badge>
                            </TableCell>
                            <TableCell className={cn("font-medium", allDeleted && "line-through")}>{group.supplier}</TableCell>
                            <TableCell className={cn("font-medium", allDeleted && "line-through")}>
                              {group.invoiceNumber || '-'}
                            </TableCell>
                            <TableCell className={cn("text-muted-foreground", allDeleted && "line-through")}>
                              {(group.receipts[0] as any)?.createdByDisplay || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {allDeleted && group.receipts[0] ? (
                                <ReceiptDeletedBy
                                  receiptId={group.receipts[0].id}
                                  restaurantId={restaurantId}
                                  deletedAt={group.receipts[0].deletedAt}
                                  updatedByDisplay={(group.receipts[0] as any).updatedByDisplay}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            {showActionsColumn && (
                              <TableCell>
                                <TooltipProvider>
                                  <div className="flex items-center gap-1 justify-end">
                                    {canEdit && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('inventory.editReceipt.editGroupDisabled')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        disabled={group.receipts.every(r => !!r.deletedAt)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Only include non-deleted receipts
                                          const nonDeletedReceipts = group.receipts.filter(r => !r.deletedAt);
                                          if (nonDeletedReceipts.length > 0) {
                                            setDeletingReceipts(nonDeletedReceipts);
                                          }
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </TooltipProvider>
                              </TableCell>
                            )}
                          </TableRow>
                          {/* Expanded Ingredient Rows */}
                          {isExpanded &&
                            group.receipts.map((receipt, idx) => {
                              const receiptDateObj = new Date(receipt.receiptDate);
                              const isLast = idx === group.receipts.length - 1;
                              const isReceiptDeleted = !!receipt.deletedAt;
                              const hasBeenEdited = receipt.updatedAt && receipt.updatedAt !== receipt.createdAt;
                              const isHistoryExpanded = expandedHistoryId === receipt.id;
                              const colSpan = showActionsColumn ? 11 : 10;
                              return (
                                <React.Fragment key={receipt.id}>
                                  <TableRow
                                    className={cn(
                                      'bg-amber-50/50 dark:bg-amber-950/20 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 border-l-4 border-l-amber-400',
                                      !isLast && !isHistoryExpanded && 'border-b border-b-amber-200/50 dark:border-b-amber-800/50',
                                      isReceiptDeleted && 'opacity-60 bg-muted/30'
                                    )}
                                  >
                                    <TableCell className="w-[50px]">
                                      <div className="flex items-center justify-center h-full">
                                        {isReceiptDeleted ? (
                                          <Badge variant="destructive" className="text-xs">
                                            {t('inventory.receiptHistory.deleted')}
                                          </Badge>
                                        ) : hasBeenEdited ? (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Button
                                                  variant={isHistoryExpanded ? 'secondary' : 'ghost'}
                                                  size="icon"
                                                  className="h-6 w-6"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedHistoryId(isHistoryExpanded ? null : receipt.id);
                                                  }}
                                                >
                                                  <History className="h-3.5 w-3.5" />
                                                </Button>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>{t('inventory.editHistory.title')}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        ) : (
                                          <div className="w-0.5 h-4 bg-amber-400/50 rounded-full"></div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className={cn('text-muted-foreground text-sm pl-4', isReceiptDeleted && 'line-through')}>
                                      {isValid(receiptDateObj)
                                        ? format(receiptDateObj, 'dd/MM/yyyy', { locale })
                                        : '-'}
                                    </TableCell>
                                    <TableCell className={cn('pl-4', isReceiptDeleted && 'line-through')}>
                                      <div className="flex items-center gap-2">
                                        <IngredientIcon
                                          ingredient={receipt.ingredient || null}
                                          size={24}
                                        />
                                        <span className="text-sm font-medium">
                                          {i18n.language === 'ar'
                                            ? receipt.ingredient?.nameAr
                                            : receipt.ingredient?.nameFr}
                                        </span>
                                      </div>
                                    </TableCell>
                                    <TableCell className={cn('text-sm pl-4', isReceiptDeleted && 'line-through')}>
                                      {receipt.quantityReceived} {receipt.unit}
                                    </TableCell>
                                    <TableCell className={cn('text-sm pl-4', isReceiptDeleted && 'line-through')}>
                                      {(Number(receipt.costPerUnit) || 0).toFixed(2)}{' '}
                                      {t('common.currency')}
                                    </TableCell>
                                    <TableCell className="pl-4">
                                      <Badge variant="outline" className={cn('font-mono text-xs', isReceiptDeleted && 'line-through')}>
                                        {(Number(receipt.totalCost) || 0).toFixed(2)}{' '}
                                        {t('common.currency')}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className={cn('text-muted-foreground text-sm pl-4', isReceiptDeleted && 'line-through')}>
                                      {receipt.supplier || '-'}
                                    </TableCell>
                            <TableCell className={cn('text-muted-foreground text-sm pl-4', isReceiptDeleted && 'line-through')}>
                              {receipt.invoiceNumber || '-'}
                            </TableCell>
                            <TableCell className={cn('text-muted-foreground text-sm pl-4', isReceiptDeleted && 'line-through')}>
                              {(receipt as any).createdByDisplay || '-'}
                            </TableCell>
                            <TableCell className="text-sm pl-4">
                              <ReceiptDeletedBy
                                receiptId={receipt.id}
                                restaurantId={restaurantId}
                                deletedAt={receipt.deletedAt}
                                updatedByDisplay={(receipt as any).updatedByDisplay}
                              />
                            </TableCell>
                                    {showActionsColumn && (
                                      <TableCell>
                                        {isReceiptDeleted ? (
                                          <span className="text-xs text-muted-foreground italic">
                                            {t('inventory.receiptHistory.alreadyDeleted')}
                                          </span>
                                        ) : (
                                          <div className="flex items-center gap-1 justify-end">
                                            {canEdit && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingReceipt(receipt);
                                                }}
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                            )}
                                            {canDelete && (
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setDeletingReceipts([receipt]);
                                                }}
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                  {/* Edit History Expandable Row */}
                                  {isHistoryExpanded && (
                                    <ReceiptEditHistoryRow receiptId={receipt.id} restaurantId={restaurantId} colSpan={colSpan} />
                                  )}
                                </React.Fragment>
                              );
                            })}
                        </React.Fragment>
                      );
                    } else {
                      // Ungrouped receipt
                      const receipt = item.data;
                      const receiptDate = new Date(receipt.receiptDate);
                      const isDeleted = !!receipt.deletedAt;
                      const hasBeenEdited = receipt.updatedAt && receipt.updatedAt !== receipt.createdAt;
                      const isHistoryExpanded = expandedHistoryId === receipt.id;
                      const colSpan = showActionsColumn ? 11 : 10;
                      return (
                        <React.Fragment key={receipt.id}>
                          <TableRow className={cn(isDeleted && 'opacity-60 bg-muted/30')}>
                            <TableCell>
                              {isDeleted ? (
                                <Badge variant="destructive" className="text-xs">
                                  {t('inventory.receiptHistory.deleted')}
                                </Badge>
                              ) : hasBeenEdited ? (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant={isHistoryExpanded ? 'secondary' : 'ghost'}
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => setExpandedHistoryId(isHistoryExpanded ? null : receipt.id)}
                                      >
                                        <History className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{t('inventory.editHistory.title')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              ) : null}
                            </TableCell>
                            <TableCell className={cn('font-medium', isDeleted && 'line-through')}>
                              {isValid(receiptDate)
                                ? format(receiptDate, 'dd/MM/yyyy', { locale })
                                : '-'}
                            </TableCell>
                            <TableCell className={cn(isDeleted && 'line-through')}>
                              <div className="flex items-center gap-2">
                                <IngredientIcon ingredient={receipt.ingredient || null} size={32} />
                                <span>
                                  {i18n.language === 'ar'
                                    ? receipt.ingredient?.nameAr
                                    : receipt.ingredient?.nameFr}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className={cn(isDeleted && 'line-through')}>
                              {receipt.quantityReceived} {receipt.unit}
                            </TableCell>
                            <TableCell className={cn(isDeleted && 'line-through')}>
                              {(Number(receipt.costPerUnit) || 0).toFixed(2)} {t('common.currency')}
                            </TableCell>
                            <TableCell>
                              <Badge variant={isDeleted ? 'outline' : 'secondary'} className={cn('font-mono', isDeleted && 'line-through')}>
                                {(Number(receipt.totalCost) || 0).toFixed(2)} {t('common.currency')}
                              </Badge>
                            </TableCell>
                            <TableCell className={cn('text-muted-foreground', isDeleted && 'line-through')}>
                              {receipt.supplier || '-'}
                            </TableCell>
                            <TableCell className={cn('text-muted-foreground', isDeleted && 'line-through')}>
                              {receipt.invoiceNumber || '-'}
                            </TableCell>
                            <TableCell className={cn('text-muted-foreground', isDeleted && 'line-through')}>
                              {(receipt as any).createdByDisplay || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              <ReceiptDeletedBy
                                receiptId={receipt.id}
                                restaurantId={restaurantId}
                                deletedAt={receipt.deletedAt}
                                updatedByDisplay={(receipt as any).updatedByDisplay}
                              />
                            </TableCell>
                            {showActionsColumn && (
                              <TableCell>
                                {isDeleted ? (
                                  <span className="text-xs text-muted-foreground italic">
                                    {t('inventory.receiptHistory.alreadyDeleted')}
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-1 justify-end">
                                    {canEdit && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingReceipt(receipt);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    )}
                                    {canDelete && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDeletingReceipts([receipt]);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                          {/* Edit History Expandable Row */}
                          {isHistoryExpanded && (
                            <ReceiptEditHistoryRow receiptId={receipt.id} restaurantId={restaurantId} colSpan={colSpan} />
                          )}
                        </React.Fragment>
                      );
                    }
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3 pt-4 border-t">
                <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left order-2 sm:order-1">
                  {t('inventory.pagination.showing', {
                    from: (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, totalCount),
                    total: totalCount,
                  })}
                </div>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="h-9"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('inventory.pagination.previous')}</span>
                  </Button>
                  <span className="text-sm font-medium text-muted-foreground min-w-[60px] text-center">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="h-9"
                  >
                    <span className="hidden sm:inline">{t('inventory.pagination.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

      {/* Edit Receipt Dialog */}
      {editingReceipt && (
        <EditReceiptDialog
          restaurantId={restaurantId}
          receipt={editingReceipt}
          open={!!editingReceipt}
          onOpenChange={(open) => !open && setEditingReceipt(null)}
        />
      )}

      {/* Delete Receipt(s) Dialog */}
      {deletingReceipts.length > 0 && (
        <DeleteReceiptDialog
          restaurantId={restaurantId}
          receipts={deletingReceipts}
          open={deletingReceipts.length > 0}
          onOpenChange={(open) => !open && setDeletingReceipts([])}
        />
      )}
    </>
  );
}
